/**
 * geocodeApi.ts — forward-geocode a typed place name via our own backend.
 *
 * Backend: GET /api/geocode?q=<query>&country=<cc>
 *   -> { results: [ { name, lat, lng }, … ] }
 *
 * This replaces Google Places Autocomplete in the trip planner. Two reasons:
 * cost (Places billing is what the `…cpYvDEM` key was locked down to stop) and
 * fragility — an API-restriction change on that key silently took the whole
 * trip-planner route step offline, because Places was its only way to turn a
 * typed place into coordinates.
 *
 * IMPORTANT — this is search-on-submit, not per-keystroke autocomplete. The
 * backend is Nominatim, whose usage policy caps requests at roughly 1/sec
 * (responses are cached an hour server-side). Callers must fire this on an
 * explicit action — Enter, or a search button — never on every keystroke.
 */

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

/** Shortest query the backend will act on; below this it returns []. */
export const MIN_GEOCODE_QUERY = 2;

export interface GeocodeResult {
  /** Full display address, e.g. "Byron Bay, New South Wales, 2481, Australia". */
  name: string;
  lat: number;
  lng: number;
}

export interface GeocodeResponse {
  results: GeocodeResult[];
  /**
   * True when the geocoder itself could not be reached or failed. Distinct from
   * an empty `results`, which means the place genuinely wasn't found — the two
   * need different wording, or a transient outage reads as "no such place".
   */
  unavailable: boolean;
}

const EMPTY: GeocodeResponse = { results: [], unavailable: false };

/** A row is only usable if we can actually put it on a map. */
function isUsable(row: unknown): row is GeocodeResult {
  if (!row || typeof row !== 'object') return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.name === 'string' &&
    r.name.trim().length > 0 &&
    typeof r.lat === 'number' &&
    Number.isFinite(r.lat) &&
    typeof r.lng === 'number' &&
    Number.isFinite(r.lng)
  );
}

/**
 * Resolve a place name to candidate coordinates.
 *
 * Never throws and never rejects: a failed lookup returns `unavailable: true`
 * with no results, so a caller renders a message instead of an error boundary.
 * An aborted request (the user searched again before this returned) resolves
 * empty and is not reported as unavailable — it isn't a failure.
 */
export async function geocodePlace(
  query: string,
  country: string,
  signal?: AbortSignal,
  near?: { lat: number; lng: number } | null
): Promise<GeocodeResponse> {
  const q = (query || '').trim();
  if (q.length < MIN_GEOCODE_QUERY) return EMPTY;

  const cc = (country || 'au').trim().toLowerCase().slice(0, 2) || 'au';
  let url = `${API_BASE}/geocode?q=${encodeURIComponent(q)}&country=${encodeURIComponent(cc)}`;
  // A bias, not a filter: the server leans Nominatim towards the trip's area
  // (viewbox, unbounded), so "Lakes Entrance" on a Melbourne–Sydney trip finds
  // the Gippsland town before the Brisbane street. Servers that predate the
  // param ignore it, and the client re-ranks by distance anyway.
  if (near && Number.isFinite(near.lat) && Number.isFinite(near.lng)) {
    url += `&near=${near.lat.toFixed(3)},${near.lng.toFixed(3)}`;
  }

  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return { results: [], unavailable: true };

    const body = await res.json();
    // The endpoint answers 200 with {results: [], error: 'geocode_unavailable'}
    // when Nominatim is down, precisely so clients degrade rather than throw.
    if (body && body.error) return { results: [], unavailable: true };

    const rows = Array.isArray(body?.results) ? body.results : [];
    return { results: rows.filter(isUsable), unavailable: false };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return EMPTY;
    return { results: [], unavailable: true };
  }
}

/* ── Reverse geocode: a dropped pin → a real place name ─────────────────── */

export interface ReversePlace {
  /** Readable stop name, e.g. "Orbost" or "Near Croajingolong National Park". */
  name: string;
  /** State abbreviation when known ("VIC"). */
  state: string | null;
}

/**
 * What a reverse lookup said about a point.
 *  • place     — on land, with a name worth showing.
 *  • water     — open sea, a bay or a lake: no stop belongs there.
 *  • unavailable — the lookup failed; the caller decides (we never guess "sea"
 *    from an outage, or a dropped connection would refuse every pin).
 */
export type ReverseResult =
  | { status: 'place'; place: ReversePlace }
  | { status: 'water' }
  | { status: 'unavailable' };

const AU_STATE_ABBR: Record<string, string> = {
  'new south wales': 'NSW',
  victoria: 'VIC',
  queensland: 'QLD',
  'south australia': 'SA',
  'western australia': 'WA',
  tasmania: 'TAS',
  'northern territory': 'NT',
  'australian capital territory': 'ACT',
};

/** Address fields that name a place you could actually stop at, best first. */
const LOCALITY_FIELDS = [
  'town',
  'city',
  'village',
  'hamlet',
  'suburb',
  'locality',
  'city_district',
  'neighbourhood',
  'isolated_dwelling',
] as const;
const FEATURE_FIELDS = ['national_park', 'nature_reserve', 'protected_area', 'park', 'leisure', 'tourism'] as const;
const AREA_FIELDS = ['municipality', 'county', 'region', 'state_district'] as const;
const WATER_TYPES = new Set(['water', 'bay', 'strait', 'sea', 'ocean', 'reef', 'lake', 'reservoir', 'wetland']);

/**
 * Turn a Nominatim reverse (jsonv2) body into a ReverseResult. Pure, so the
 * land/water rules are unit-tested without a network.
 *
 * The rule that matters: in Australia OSM carries a locality boundary over
 * essentially all land (ABS suburbs/localities import). A point that resolves
 * to nothing, or only to a state or country, is offshore — Nominatim hands
 * back "Victoria" for Bass Strait because state boundaries run 3 nm out.
 */
export function parseReverse(body: unknown): ReverseResult {
  if (!body || typeof body !== 'object') return { status: 'water' };
  const b = body as Record<string, unknown>;
  if (b.error) return { status: 'water' };
  const addr = (b.address && typeof b.address === 'object' ? b.address : {}) as Record<string, string>;
  const category = String(b.category ?? b.class ?? '');
  const type = String(b.type ?? '');
  if ((category === 'natural' || category === 'water' || category === 'waterway') && WATER_TYPES.has(type)) {
    return { status: 'water' };
  }
  const state = AU_STATE_ABBR[(addr.state || '').toLowerCase()] ?? null;
  const locality = LOCALITY_FIELDS.map((f) => addr[f]).find((v) => typeof v === 'string' && v.trim());
  if (locality) return { status: 'place', place: { name: locality.trim(), state } };
  const feature = FEATURE_FIELDS.map((f) => addr[f]).find((v) => typeof v === 'string' && v.trim());
  if (feature) return { status: 'place', place: { name: `Near ${feature.trim()}`, state } };
  const area = AREA_FIELDS.map((f) => addr[f]).find((v) => typeof v === 'string' && v.trim());
  if (area) {
    const clean = area.replace(/^(shire|city|municipality|rural city|town|borough|district council|regional council) of /i, '').trim();
    return { status: 'place', place: { name: `Near ${clean}`, state } };
  }
  // Only a state or country (addresstype 'state'/'country'), or nothing
  // usable at all — that's the sea.
  return { status: 'water' };
}

const NOMINATIM_REVERSE = 'https://nominatim.openstreetmap.org/reverse';

/**
 * Name the place under a dropped pin, or say it's in the water.
 *
 * Asks our backend first (/api/reverse-geocode — cached, and it identifies us
 * to Nominatim properly). A server without that route (204/404) falls through to
 * Nominatim directly: one request per deliberate pin drop is well inside its
 * usage policy, and CORS is open. Never throws.
 */
export async function reverseGeocodePlace(
  lat: number,
  lng: number,
  signal?: AbortSignal
): Promise<ReverseResult> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { status: 'unavailable' };
  const qs = `lat=${lat.toFixed(5)}&lng=${lng.toFixed(5)}`;
  try {
    const res = await fetch(`${API_BASE}/reverse-geocode?${qs}`, { signal });
    // 204/404: a server that predates the route (unknown paths answer 204).
    if (res.ok && res.status !== 204) {
      const body = await res.json();
      if (body && body.error === 'reverse_unavailable') return { status: 'unavailable' };
      if (body && body.water) return { status: 'water' };
      if (body && typeof body.name === 'string' && body.name.trim()) {
        return { status: 'place', place: { name: body.name.trim(), state: body.state ?? null } };
      }
      return { status: 'water' };
    }
    if (res.status !== 404 && res.status !== 204) return { status: 'unavailable' };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return { status: 'unavailable' };
    // Fall through to Nominatim — our API being down shouldn't block a pin.
  }
  try {
    const res = await fetch(
      `${NOMINATIM_REVERSE}?format=jsonv2&zoom=14&addressdetails=1&lat=${lat.toFixed(5)}&lon=${lng.toFixed(5)}`,
      { signal, headers: { Accept: 'application/json' } }
    );
    if (!res.ok) return { status: 'unavailable' };
    return parseReverse(await res.json());
  } catch {
    return { status: 'unavailable' };
  }
}

/* ── Nearest land: a pin in the sea → the closest named spot on the coast ── */

export interface NearbyTown {
  name: string;
  lat: number;
  lng: number;
  /** Straight-line distance from the refused point. */
  km: number;
}

export interface LandPoint {
  lat: number;
  lng: number;
  name?: string;
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Beyond this, "nearest land" would be a different trip. */
export const MAX_SNAP_KM = 200;

/**
 * The closest known point on land (our listings sit on land by definition)
 * within MAX_SNAP_KM. Pure, so the rule is unit-tested.
 */
export function pickNearestPoint(points: LandPoint[], lat: number, lng: number): (LandPoint & { km: number }) | null {
  let best: (LandPoint & { km: number }) | null = null;
  for (const p of points) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng) || (p.lat === 0 && p.lng === 0)) continue;
    const km = haversineKm({ lat, lng }, p);
    if (km <= MAX_SNAP_KM && (!best || km < best.km)) best = { ...p, km };
  }
  return best;
}

/**
 * Where a refused sea pin should go instead: the nearest listing (they're
 * all on land), named by its locality ("Marlo"), so the offer reads
 * "Nearest town is Marlo, 12 km north · Add Marlo instead". `loadPoints` supplies the points (the map's
 * cached all-sites list). Null when there's nothing within reach. Never throws.
 */
export async function nearestLand(
  lat: number,
  lng: number,
  loadPoints: () => Promise<LandPoint[]>,
  signal?: AbortSignal
): Promise<NearbyTown | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    const hit = pickNearestPoint(await loadPoints(), lat, lng);
    if (!hit) return null;
    const where = await reverseGeocodePlace(hit.lat, hit.lng, signal);
    const name = where.status === 'place' ? where.place.name : hit.name?.trim();
    if (!name) return null;
    // The offer names a town, so the pin goes ON that town, not on the
    // listing we found it through (a campground 5 km down the coast put
    // "Tamboon" on the beach with its halo in the sea).
    const state = where.status === 'place' ? where.place.state : null;
    const centre = await townCentre(name, state, hit, signal);
    const at = centre ?? hit;
    return { name, lat: at.lat, lng: at.lng, km: haversineKm({ lat, lng }, at) };
  } catch {
    return null;
  }
}

const COMPASS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

/**
 * The way from `from` to `to` in words ("north", "south-west"), for copy like
 * "Nearest town is Tamboon, 86 km north". Eight points: finer reads as false
 * precision for a town tens of km away.
 */
export function compassWord(from: { lat: number; lng: number }, to: { lat: number; lng: number }): string {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(to.lng - from.lng)) * Math.cos(toRad(to.lat));
  const x =
    Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) -
    Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(toRad(to.lng - from.lng));
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  return COMPASS[Math.round(bearing / 45) % 8];
}

/** A named town's centre may sit this far from the listing that found it. */
export const TOWN_CENTRE_MAX_KM = 40;

/**
 * The centre of the town called `name` near `near` (forward geocode, biased to
 * that spot). Null for a "Near <park>" name, a miss, or a same-named place
 * somewhere else. Never throws.
 */
export async function townCentre(
  name: string,
  state: string | null,
  near: { lat: number; lng: number },
  signal?: AbortSignal
): Promise<{ lat: number; lng: number } | null> {
  if (!name || /^near\s/i.test(name)) return null;
  const q = state ? `${name}, ${state}` : name;
  const { results } = await geocodePlace(q, 'au', signal, near);
  const want = name.trim().toLowerCase();
  const match = results.find(
    (r) =>
      r.name.split(',')[0].trim().toLowerCase() === want &&
      haversineKm(near, r) <= TOWN_CENTRE_MAX_KM
  );
  return match ? { lat: match.lat, lng: match.lng } : null;
}
